import React from "react";
import { motion } from "framer-motion";
import { Star, Quotes, ArrowsHorizontal } from "@phosphor-icons/react";

const TESTIMONIALS = [
  {
    quote:
      "Replace this with a real review. Talk about how the team showed up on time, did exactly what they said, and left the place better than they found it.",
    name: "Customer Name",
    role: "Hixson, TN · Bi-weekly cleaning",
  },
  {
    quote:
      "Drop in a customer's words about a handyman project — TV mount, ceiling fan, fence repair — and how easy it was to schedule.",
    name: "Customer Name",
    role: "Cleveland, TN · Handyman + odd jobs",
  },
  {
    quote:
      "Use this third spot for an office or commercial client. Mention turnaround time, communication, or a recurring plan that fits their team.",
    name: "Office Manager",
    role: "Chattanooga, TN · Weekly office cleaning",
  },
  {
    quote:
      "Optional fourth quote for an event or move-in/move-out cleanup story.",
    name: "Customer Name",
    role: "Ooltewah, TN · Event cleanup",
  },
];

// Branded SVG placeholders — clearly placeholders so the owner can replace with real job photos.
const placeholder = (label, palette) => {
  const [bg, accent, text] = palette;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 600'>
    <defs>
      <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${bg}'/>
        <stop offset='100%' stop-color='${accent}'/>
      </linearGradient>
      <pattern id='dots' width='28' height='28' patternUnits='userSpaceOnUse'>
        <circle cx='2' cy='2' r='1.6' fill='${text}' fill-opacity='0.18'/>
      </pattern>
    </defs>
    <rect width='800' height='600' fill='url(%23g)'/>
    <rect width='800' height='600' fill='url(%23dots)'/>
    <text x='50%' y='50%' text-anchor='middle' dominant-baseline='middle'
      font-family='Outfit, sans-serif' font-weight='700' font-size='42' fill='${text}'>${label}</text>
    <text x='50%' y='62%' text-anchor='middle' dominant-baseline='middle'
      font-family='Manrope, sans-serif' font-size='18' fill='${text}' fill-opacity='0.7'>Replace with real photo</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${svg.replace(/\n/g, "").replace(/#/g, "%23")}`;
};

const BEFORE_AFTER = [
  {
    label: "Kitchen deep clean",
    before: placeholder("BEFORE · Kitchen", ["#fef3c7", "#fde68a", "#92400e"]),
    after: placeholder("AFTER · Kitchen", ["#e0f2fe", "#bae6fd", "#075985"]),
  },
  {
    label: "Yard refresh",
    before: placeholder("BEFORE · Yard", ["#fef3c7", "#fde68a", "#92400e"]),
    after: placeholder("AFTER · Yard", ["#e0f2fe", "#bae6fd", "#075985"]),
  },
  {
    label: "Living room reset",
    before: placeholder("BEFORE · Room", ["#fef3c7", "#fde68a", "#92400e"]),
    after: placeholder("AFTER · Room", ["#e0f2fe", "#bae6fd", "#075985"]),
  },
];

export default function HappyCustomers() {
  return (
    <section
      id="happy-customers"
      className="py-24 md:py-32 bg-white"
      data-testid="happy-customers-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
            Happy Customers
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
            Real neighbors. Real results.
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Reviews and project photos go here — swap the placeholders below with your own customer
            quotes and before/after pictures whenever you&apos;re ready.
          </p>
        </div>

        {/* Testimonials */}
        <div
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="testimonials-grid"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="relative bg-stone-50 border border-slate-100 rounded-3xl p-7 hover:-translate-y-1 hover:border-sky-200 transition-all duration-300"
              data-testid={`testimonial-${i}`}
            >
              <Quotes
                size={32}
                weight="fill"
                className="absolute -top-3 left-6 text-sky-500/80"
              />
              <div className="flex items-center gap-1 text-amber-400">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={16} weight="fill" />
                ))}
              </div>
              <blockquote className="mt-4 text-[15px] text-slate-700 leading-relaxed">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-5 pt-5 border-t border-slate-200">
                <div className="font-display font-semibold text-slate-900 text-sm">
                  {t.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{t.role}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Before / After gallery */}
        <div className="mt-20" data-testid="before-after-gallery">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
                Before &amp; After
              </div>
              <h3 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-slate-900">
                A small taste of what we do.
              </h3>
            </div>
            <p className="text-sm text-slate-500 max-w-sm">
              These are placeholders — replace with your own job photos any time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BEFORE_AFTER.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-white border border-slate-100 rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                data-testid={`before-after-${i}`}
              >
                <div className="grid grid-cols-2 relative">
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={item.before}
                      alt={`${item.label} — before`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2 bg-slate-900/80 text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full">
                      Before
                    </div>
                  </div>
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={item.after}
                      alt={`${item.label} — after`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-sky-500 text-white text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full">
                      After
                    </div>
                  </div>
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-9 flex items-center justify-center pointer-events-none">
                    <div className="bg-white rounded-full p-2 shadow-md border border-slate-200">
                      <ArrowsHorizontal size={16} weight="bold" className="text-sky-500" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-display font-semibold text-slate-900">{item.label}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Replace this caption with the project details.
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
